import { setFilterData } from "@/utils/redux/fetchOffers";
import { Box, Text } from "@dxp-feed/ui";
import React, { useEffect } from "react";
import { useState, useRef } from "react";
import { useDispatch } from "react-redux";

interface PriceRangeSliderProps {
  minPrice?: number;
  maxPrice?: number;
  initialMin?: number;
  initialMax?: number;
  midPoint?: number;
  onChange?: (min: number, max: number) => void;
  onChangeEnd?: (min: number, max: number) => void;
  resetTrigger?: number;
}

const PriceRangeSlider: React.FC<PriceRangeSliderProps> = ({
  minPrice = 1,
  maxPrice = 99,
  initialMin = 20,
  initialMax = 75,
  midPoint = 50,
  onChange,
  onChangeEnd,
  resetTrigger,
}) => {
  const dispatch = useDispatch();
  const [minValue, setMinValue] = useState(initialMin);
  const [maxValue, setMaxValue] = useState(initialMax);
  const [isDragging, setIsDragging] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const minThumbRef = useRef<HTMLInputElement | null>(null);
  const maxThumbRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setMinValue(initialMin);
    setMaxValue(initialMax);
  }, [resetTrigger]);

  useEffect(() => {
    setMinValue(minPrice);
    setMaxValue(maxPrice);
  }, [minPrice, maxPrice]);

  const valueToPercent = (value: number) => {
    return ((value - minPrice) / (maxPrice - minPrice)) * 100;
  };

  const positionToValue = (position: number) => {
    if (!sliderRef.current) return 0;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(100, ((position - sliderRect.left) / sliderRect.width) * 100)
    );
    dispatch(
      setFilterData({
        filter: "price",
        filterName: percent,
        filterValue: percent,
      })
    );
    return Math.round((percent / 100) * (maxPrice - minPrice) + minPrice);
  };

  const handleThumbMouseDown = (isMin: boolean) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newValue = positionToValue(moveEvent.clientX);

      if (isMin) {
        setMinValue((prevMin) => {
          const updatedMin = Math.min(
            Math.max(minPrice, newValue),
            maxValue - 1
          );
          if (prevMin !== updatedMin) {
            onChange?.(updatedMin, maxValue);
          }
          return updatedMin;
        });
      } else {
        setMaxValue((prevMax) => {
          const updatedMax = Math.max(
            Math.min(maxPrice, newValue),
            minValue + 1
          );
          if (prevMax !== updatedMax) {
            onChange?.(minValue, updatedMax);
          }
          return updatedMax;
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setMinValue((prevMin) => {
        setMaxValue((prevMax) => {
          onChangeEnd?.(prevMin, prevMax);
          return prevMax;
        });
        return prevMin;
      });

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleThumbTouchStart = (isMin: boolean) => (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const newValue = positionToValue(moveEvent.touches[0].clientX);

      if (isMin) {
        setMinValue((prevMin) => {
          const updatedMin = Math.min(
            Math.max(minPrice, newValue),
            maxValue - 1
          );

          if (prevMin === updatedMin) return prevMin;

          onChange?.(updatedMin, maxValue);
          return updatedMin;
        });
      } else {
        setMaxValue((prevMax) => {
          const updatedMax = Math.max(
            Math.min(maxPrice, newValue),
            minValue + 1
          );

          if (prevMax === updatedMax) return prevMax;

          onChange?.(minValue, updatedMax);
          return updatedMax;
        });
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setMinValue((prevMin) => {
        setMaxValue((prevMax) => {
          onChangeEnd?.(prevMin, prevMax);
          return prevMax;
        });
        return prevMin;
      });

      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    const clickValue = positionToValue(e.clientX);
    let newMin = minValue;
    let newMax = maxValue;

    if (Math.abs(clickValue - minValue) <= Math.abs(clickValue - maxValue)) {
      newMin = Math.min(Math.max(minPrice, clickValue), maxValue - 1);
      setMinValue(newMin);
    } else {
      newMax = Math.max(Math.min(maxPrice, clickValue), minValue + 1);
      setMaxValue(newMax);
    }

    onChange?.(newMin, newMax);

    setTimeout(() => {
      onChangeEnd?.(newMin, newMax);
    }, 0);
  };

  const minValuePercent = valueToPercent(minValue);
  const maxValuePercent = valueToPercent(maxValue);

  if (minPrice === 0 && maxPrice === 0) {
    return <></>;
  }

  return (
    <Box as="div" className="w-full lg:max-w-md mx-auto mt-4">
      <Box as="div" className="text-body text-brandBlack font-bold mb-2">
        Price
      </Box>
      <Box
        as="div"
        className="text-center text-body text-brandBlack font-bold mb-1"
      >
        ${minValue} - ${maxValue >= 99 ? "99+" : maxValue}
      </Box>

      <Box as="div" className="relative lg:mx-[15px] sm:mx-[33px] mx-[26px]">
        <Box
          as="div"
          ref={sliderRef}
          // aria-label={sliderRef}
          className="relative h-[6px] bg-grey350 rounded-full"
          onClick={handleTrackClick}
        >
          <Box
            as="div"
            className="absolute h-full bg-brandPrimary rounded-full"
            style={{
              left: `${minValuePercent}%`,
              width: `${maxValuePercent - minValuePercent}%`,
            }}
          />
        </Box>
        <label htmlFor="minThumb" className="sr-only">
          Minimum Price
        </label>
        <input
          type="range"
          id="minThumb"
          ref={minThumbRef}
          className={`appearance-none absolute w-[22px] h-[22px] top-[-8px] 
            bg-brandWhite border-4 border-brandPrimary rounded-full 
            transform -translate-x-1/2 
            focus:outline-none focus:ring-0 
            [&::-webkit-slider-thumb]:appearance-none 
            [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] 
            [&::-webkit-slider-thumb]:bg-brandWhite 
            [&::-webkit-slider-thumb]:rounded-full 
            [&::-moz-range-thumb]:appearance-none 
            [&::-moz-range-thumb]:width-[10px] [&::-moz-range-thumb]:height-[10px] 
            [&::-moz-range-thumb]:bg-transparent
            [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:border-brandPrimary 
            [&::-moz-range-thumb]:rounded-full
            ${isDragging ? "cursor-grabbing" : "cursor-pointer"}`}
          style={{
            left: `${minValuePercent}%`,
          }}
          min={minPrice}
          max={maxValue}
          value={minValue}
          onChange={(e) => setMinValue(Number(e.target.value))}
          onMouseDown={handleThumbMouseDown(true)}
          onTouchStart={handleThumbTouchStart(true)}
          onKeyUp={() => onChangeEnd?.(minValue, maxValue)}
        />
        <label htmlFor="maxThumb" className="sr-only">
          Maximum Price
        </label>
        <input
          type="range"
          id="maxThumb"
          ref={maxThumbRef}
          className={`appearance-none absolute w-[22px] h-[22px] top-[-8px] 
            bg-brandWhite border-4 border-brandPrimary rounded-full 
            transform -translate-x-1/2 
            focus:outline-none focus:ring-0 
            [&::-webkit-slider-thumb]:appearance-none 
            [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] 
            [&::-webkit-slider-thumb]:bg-brandWhite 
            [&::-webkit-slider-thumb]:rounded-full 
            [&::-moz-range-thumb]:appearance-none 
            [&::-moz-range-thumb]:width-[10px] [&::-moz-range-thumb]:height-[10px] 
            [&::-moz-range-thumb]:bg-transparent
            [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:border-brandPrimary 
            [&::-moz-range-thumb]:rounded-full
            ${isDragging ? "cursor-grabbing" : "cursor-pointer"}`}
          style={{
            left: `${maxValuePercent}%`,
          }}
          min={minValue}
          max={maxPrice}
          value={maxValue}
          onChange={(e) => setMaxValue(Number(e.target.value))}
          onMouseDown={handleThumbMouseDown(false)}
          onTouchStart={handleThumbTouchStart(false)}
          onKeyUp={() => onChangeEnd?.(minValue, maxValue)}
        />
        <Box
          as="div"
          className="flex justify-between text-body text-brandBlack mt-2"
        >
          <Text as="span">${minPrice}</Text>
          <Text as="span">${midPoint}</Text>
          <Text as="span">${maxPrice >= 100 ? "99" : maxPrice}+</Text>
        </Box>
      </Box>
    </Box>
  );
};

export default PriceRangeSlider;
